import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, List, ListItem, ListItemAvatar, ListItemText, Avatar, Divider, CircularProgress, Tooltip, Chip, } from '@mui/material';
import { Send, SmartToy, AccountCircle, Person, Chat as ChatIcon, DoneAll as DoneAllIcon, Done as DoneIcon, ErrorOutline as ErrorOutlineIcon, } from '@mui/icons-material';
import { useAppContext } from '../contexts/appContext';
import { MessageDeliveryStatus, renderTimeUtc } from '@common/clientServerApi';
import { AppEventType } from '../app/appEvents';
import { viewerTime } from '../viewerTime';
import { MessageChannelBadge } from './messageChannelBadge';
import { MessageDeliveryNote } from './messageDeliveryNote';
const ChatComponent = ({ chatId, title = 'Conversation', placeholder = 'Type a message...', height = '100%', userRole = 'candidate', showHeader = true, enableInput = true, participantRoles, }) => {
    const { app } = useAppContext();
    const currentUser = app.getCurrentUser();
    // Recruiters see times in their profile's zone; admins in their own browser's.
    const formatRetryTime = (instant) => (userRole === 'recruiter'
        ? renderTimeUtc(instant, app.getCurrentUserTimezone())
        : viewerTime(instant));
    const [messages, setMessages] = useState(() => app.getCachedChatMessages(chatId));
    const [loading, setLoading] = useState(() => !app.isChatLoaded(chatId));
    const [hasEarlier, setHasEarlier] = useState(() => app.hasEarlierChatMessages(chatId));
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadingEarlier, setLoadingEarlier] = useState(false);
    const [earlierFailed, setEarlierFailed] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const scrollRef = useRef(null);
    // True while the view sits at the newest message; a manual scroll-up clears
    // it so incoming messages don't yank the reader back down.
    const pinnedToBottomRef = useRef(true);
    // Earlier messages were put in front: keep the reader on the message they were reading.
    const heightBeforeEarlierRef = useRef(null);
    const activeChatRef = useRef(chatId);
    const loadNewest = () => {
        setLoadFailed(false);
        setLoading(!app.isChatLoaded(chatId));
        app.getChatMessages(chatId)
            .catch(() => {
            if (activeChatRef.current === chatId)
                setLoadFailed(true);
        })
            .finally(() => {
            if (activeChatRef.current === chatId)
                setLoading(false);
        });
    };
    // Render what's cached, load the newest page (conversations load when opened), and follow updates.
    useEffect(() => {
        activeChatRef.current = chatId;
        setNewMessage(''); // a draft belongs to the chat it was typed in
        setMessages(app.getCachedChatMessages(chatId));
        setHasEarlier(app.hasEarlierChatMessages(chatId));
        setLoadingEarlier(false);
        setEarlierFailed(false);
        heightBeforeEarlierRef.current = null;
        pinnedToBottomRef.current = true; // a freshly opened chat starts at the bottom
        const handler = (event) => {
            if (event.chatId !== chatId)
                return;
            if (event.earlierLoaded)
                heightBeforeEarlierRef.current = scrollRef.current?.scrollHeight ?? null;
            setMessages(event.messages);
            setHasEarlier(app.hasEarlierChatMessages(chatId));
        };
        app.on(AppEventType.CHAT_MESSAGE_RECEIVED, handler);
        loadNewest();
        return () => {
            app.off(AppEventType.CHAT_MESSAGE_RECEIVED, handler);
        };
    }, [chatId, app]);
    // Earlier messages went in front: shift by their height so the view doesn't jump.
    useLayoutEffect(() => {
        const heightBefore = heightBeforeEarlierRef.current;
        const el = scrollRef.current;
        if (heightBefore === null || !el)
            return;
        heightBeforeEarlierRef.current = null;
        el.scrollTop += el.scrollHeight - heightBefore;
    }, [messages]);
    const handleShowEarlier = () => {
        pinnedToBottomRef.current = false;
        setLoadingEarlier(true);
        setEarlierFailed(false);
        app.loadEarlierChatMessages(chatId)
            .catch(() => {
            if (activeChatRef.current === chatId)
                setEarlierFailed(true);
        })
            .finally(() => {
            if (activeChatRef.current === chatId)
                setLoadingEarlier(false);
        });
    };
    // Auto-scroll to the newest message — but only when the reader is already
    // at the bottom, so a manual scroll-up to read history isn't interrupted.
    useEffect(() => {
        if (!pinnedToBottomRef.current)
            return;
        const el = scrollRef.current;
        if (!el)
            return;
        const id = requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
        return () => cancelAnimationFrame(id);
    }, [messages]);
    // Re-pin only when the reader scrolls back to (near) the bottom.
    const handleScroll = () => {
        const el = scrollRef.current;
        if (el)
            pinnedToBottomRef.current = el.scrollHeight - el.clientHeight - el.scrollTop < 80;
    };
    const handleSendMessage = () => {
        if (!newMessage.trim() || !chatId || !app)
            return;
        const messageText = newMessage;
        setNewMessage(''); // Clear input immediately for better UX
        pinnedToBottomRef.current = true; // sending always jumps to your own message
        app.sendChatMessage(chatId, messageText);
    };
    const determineMessageType = (message) => {
        // Prefer the resolved role map (admin view) when available — it's authoritative.
        const resolved = participantRoles?.[message.senderId]?.role;
        if (resolved) {
            if (resolved === 'agent')
                return 'agent';
            if (resolved === 'system')
                return 'system';
            if (resolved === 'recruiter')
                return 'recruiter';
            if (resolved === 'candidate')
                return 'candidate';
        }
        // The map covers the candidate, the agent and the system: anyone else writing is a teammate.
        if (participantRoles && !resolved)
            return 'recruiter';
        // Handle system and AI agent messages first
        if (message.senderId === 'system')
            return 'system';
        if (message.senderId === 'recruiting-agent')
            return 'agent';
        // Check if it's the current user
        if (currentUser && message.senderId === currentUser.uid) {
            return userRole;
        }
        // Use sender name as fallback for other users
        if (message.senderName?.toLowerCase().includes('recruiter'))
            return 'recruiter';
        if (message.senderName?.toLowerCase().includes('candidate'))
            return 'candidate';
        // Default based on role (opposite of current user)
        return userRole === 'candidate' ? 'recruiter' : 'candidate';
    };
    const getMessageAvatar = (messageType) => {
        switch (messageType) {
            case 'system':
                return _jsx(SmartToy, {});
            case 'agent':
                return _jsx(SmartToy, {});
            case 'recruiter':
                return _jsx(AccountCircle, {});
            case 'candidate':
                return _jsx(Person, {});
            default:
                return _jsx(Person, {});
        }
    };
    const getMessageColor = (messageType) => {
        switch (messageType) {
            case 'system':
                return '#1976d2'; // Blue for system messages
            case 'agent':
                return '#616161'; // Light grey for AI recruiting agent (close to f2f2f2 but darker for readability)
            case 'recruiter':
                return '#424242'; // Dark grey for recruiters  
            case 'candidate':
                return '#2196f3'; // Neutral blue for candidates
            default:
                return '#757575'; // Grey for others
        }
    };
    const getMessageBackgroundColor = (messageType, isCurrentUser) => {
        if (isCurrentUser) {
            return 'rgba(25, 118, 210, 0.1)'; // Light blue for current user
        }
        switch (messageType) {
            case 'system':
                return 'rgba(25, 118, 210, 0.05)'; // Very light blue
            case 'agent':
                return 'rgba(97, 97, 97, 0.03)'; // Very light grey background for agent
            case 'recruiter':
                return 'rgba(66, 66, 66, 0.05)'; // Very light dark grey
            case 'candidate':
                return 'rgba(33, 150, 243, 0.05)'; // Very light blue for candidates
            default:
                return 'rgba(117, 117, 117, 0.05)'; // Very light grey
        }
    };
    // The conversation isn't loaded yet: say so even when a message is already shown (one that arrived live).
    const loadStatus = loading ? (_jsxs(_Fragment, { children: [_jsx(CircularProgress, { size: 18 }), _jsx(Typography, { color: "text.secondary", children: "Loading conversation\u2026" })] })) : loadFailed ? (_jsxs(_Fragment, { children: [_jsx(Typography, { color: "error.main", children: "Couldn't load this conversation." }), _jsx(Button, { size: "small", onClick: loadNewest, children: "Try again" })] })) : null;
    return (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            height: height,
            minHeight: 400
        }, children: [showHeader && (_jsx(Box, { sx: { p: 2, borderBottom: 1, borderColor: 'divider' }, children: _jsxs(Typography, { variant: "h6", sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(ChatIcon, {}), " ", title] }) })), _jsxs(Box, { ref: scrollRef, onScroll: handleScroll, "data-testid": "chat-messages", sx: { flex: 1, overflow: 'auto', p: 2 }, children: [hasEarlier && (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mb: 1.5 }, children: [_jsx(Button, { size: "small", onClick: handleShowEarlier, disabled: loadingEarlier, startIcon: loadingEarlier ? _jsx(CircularProgress, { size: 14 }) : undefined, children: loadingEarlier ? 'Loading…' : 'Show earlier messages' }), earlierFailed && (_jsx(Typography, { variant: "caption", color: "error.main", children: "Couldn't load earlier messages. Try again." }))] })), messages.length > 0 && loadStatus && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1.5 }, children: loadStatus })), messages.length === 0 ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, p: 4 }, children: loadStatus ?? (_jsx(Typography, { color: "text.secondary", children: "No messages yet. Start the conversation!" })) })) : (_jsx(List, { sx: { p: 0 }, children: messages.map((message) => {
                            const messageType = determineMessageType(message);
                            const isCurrentUser = Boolean(currentUser && message.senderId === currentUser.uid);
                            return (_jsxs(ListItem, { "data-testid": "chat-message", sx: {
                                    px: 0,
                                    alignItems: 'flex-start',
                                    backgroundColor: getMessageBackgroundColor(messageType, isCurrentUser),
                                    borderRadius: 1,
                                    mb: 1,
                                    p: 1.5,
                                    borderLeft: `3px solid ${getMessageColor(messageType)}`,
                                }, children: [_jsx(ListItemAvatar, { children: _jsx(Avatar, { sx: { bgcolor: getMessageColor(messageType) }, children: getMessageAvatar(messageType) }) }), _jsx(ListItemText, { primary: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }, children: [_jsx(Typography, { variant: "body2", fontWeight: 600, sx: { color: getMessageColor(messageType) }, children: isCurrentUser ? 'You' : (participantRoles?.[message.senderId]?.name || message.senderName || messageType) }), participantRoles && (_jsx(Chip, { size: "small", label: messageType === 'agent' ? 'AI Agent' :
                                                        messageType === 'system' ? 'System' :
                                                            messageType === 'recruiter' ? 'Recruiter' :
                                                                messageType === 'candidate' ? 'Candidate' :
                                                                    'Unknown', sx: {
                                                        height: 20,
                                                        fontSize: '0.8125rem',
                                                        fontWeight: 600,
                                                        bgcolor: getMessageColor(messageType),
                                                        color: 'common.white',
                                                        '& .MuiChip-label': { px: 0.75 },
                                                    } })), _jsx(Typography, { variant: "caption", color: "text.secondary", children: new Date(message.timestampUtc).toLocaleString() }), message.channel && userRole !== 'candidate' && (_jsx(MessageChannelBadge, { channel: message.channel }))] }), secondary: _jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body2", sx: { mt: 0.5 }, children: message.text }), userRole !== 'candidate' && (_jsx(MessageDeliveryNote, { message: message, formatTime: formatRetryTime }))] }), secondaryTypographyProps: { component: 'div' } }), isCurrentUser && (_jsx(Box, { sx: { display: 'flex', alignItems: 'flex-end', pb: 0.5, pr: 0.5 }, children: message.deliveryStatus === MessageDeliveryStatus.Failed ? (_jsx(Tooltip, { title: message.deliveryError || 'Not delivered', arrow: true, children: _jsx(ErrorOutlineIcon, { "aria-label": "Not delivered", sx: { fontSize: 16, color: 'error.main', cursor: 'pointer' } }) })) : (_jsxs(_Fragment, { children: [message.status === 'pending' && (_jsx(CircularProgress, { size: 12, sx: { color: 'text.disabled' } })), message.status === 'sent' && (_jsx(DoneAllIcon, { sx: { fontSize: 16, color: 'primary.main' } })), message.status === 'failed' && (_jsx(ErrorOutlineIcon, { sx: { fontSize: 16, color: 'error.main' } }))] })) })), !isCurrentUser && message.deliveryStatus && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-end', pb: 0.5, pr: 0.5, gap: 0.5 }, children: [message.deliveryStatus === MessageDeliveryStatus.SentToChat && (_jsx(DoneIcon, { sx: { fontSize: 14, color: 'text.disabled' } })), message.deliveryStatus === MessageDeliveryStatus.Delivered && (_jsx(DoneAllIcon, { sx: { fontSize: 14, color: 'success.main' } })), message.deliveryStatus === MessageDeliveryStatus.Failed && (_jsx(Tooltip, { title: message.deliveryError || 'Delivery failed', arrow: true, children: _jsx(ErrorOutlineIcon, { sx: { fontSize: 14, color: 'error.main', cursor: 'pointer' } }) }))] }))] }, message.id));
                        }) }))] }), enableInput && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(Box, { sx: { p: 2 }, children: _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(TextField, { fullWidth: true, multiline: true, maxRows: 3, placeholder: placeholder, value: newMessage, onChange: (e) => setNewMessage(e.target.value), slotProps: { htmlInput: { 'data-testid': 'chat-input' } }, onKeyPress: (e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    } }), _jsx(Button, { "data-testid": "chat-send-button", variant: "contained", onClick: handleSendMessage, disabled: !newMessage.trim(), sx: { minWidth: 'auto', px: 2 }, children: _jsx(Send, {}) })] }) })] }))] }));
};
export default ChatComponent;
